/**
 * Catégories de documents (phase 3).
 *
 * Réglage de l'espace, sur le modèle des champs techniques : rien n'est imposé
 * par le produit, un jeu de départ s'installe à la demande, et chaque catégorie
 * se crée ou s'archive librement.
 *
 * Une catégorie peut être réservée à certains objets : un « plan d'accès » n'a
 * pas de sens sur une machine, une « plaque signalétique » pas davantage sur un
 * site. La réserver évite d'encombrer la liste de choix au moment de classer.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { ApiError, api, type CategorieDocument, type OwnerType } from '../api/client';

const TYPES: readonly { code: OwnerType; libelle: string }[] = [
  { code: 'asset', libelle: 'Machines' },
  { code: 'site', libelle: 'Sites' },
  { code: 'party', libelle: 'Tiers' },
];

export function DocumentCategoriesPage() {
  const [categories, setCategories] = useState<CategorieDocument[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [types, setTypes] = useState<OwnerType[]>([]);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      setCategories(await api.documentCategories());
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Catégories illisibles.');
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const installer = async () => {
    setErreur(null);
    setSucces(null);
    try {
      const r = await api.seedDocumentCategories();
      await charger();
      setSucces(
        r.created === 0
          ? 'Le jeu de départ est déjà installé, rien n’a été ajouté.'
          : `${r.created} catégorie${r.created > 1 ? 's' : ''} ajoutée${r.created > 1 ? 's' : ''}.`,
      );
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Installation impossible.');
    }
  };

  const creer = async (e: FormEvent) => {
    e.preventDefault();
    if (label.trim() === '') return;

    setEnCours(true);
    setErreur(null);
    setSucces(null);
    try {
      await api.createDocumentCategory({ label: label.trim(), appliesTo: types });
      setLabel('');
      setTypes([]);
      await charger();
      setSucces('Catégorie créée.');
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Création impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const archiver = async (categorie: CategorieDocument) => {
    if (
      !window.confirm(
        `Archiver « ${categorie.label} » ?\n\n` +
          'Les documents déjà classés la gardent ; elle ne sera simplement plus proposée.',
      )
    ) {
      return;
    }
    setErreur(null);
    setSucces(null);
    try {
      await api.archiveDocumentCategory(categorie.id);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  const basculer = (code: OwnerType) =>
    setTypes((liste) => (liste.includes(code) ? liste.filter((x) => x !== code) : [...liste, code]));

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Catégories de documents</h1>
          <p>
            Pour classer notices, rapports de contrôle et plans. Aucune catégorie n’est imposée :
            installez le jeu de départ ou créez les vôtres.
          </p>
        </div>
        <div className="actions">
          <button className="secondaire" onClick={() => void installer()}>
            Installer le jeu de départ
          </button>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {succes && <div className="message succes">{succes}</div>}

      <div className="carte">
        <h2>Nouvelle catégorie</h2>
        <div className="carte-corps">
          <form className="saisie-ligne" onSubmit={(e) => void creer(e)}>
            <input
              type="text"
              placeholder="Libellé : « Rapport d’étanchéité »"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              aria-label="Libellé de la catégorie"
            />
            <span className="aide">Proposée pour :</span>
            {TYPES.map((t) => (
              <label key={t.code} className="case-usage">
                <input
                  type="checkbox"
                  checked={types.includes(t.code)}
                  onChange={() => basculer(t.code)}
                />
                <span>{t.libelle}</span>
              </label>
            ))}
            <button disabled={enCours || label.trim() === ''}>
              {enCours ? 'Création…' : 'Créer'}
            </button>
          </form>
          <p className="aide">Aucune case cochée : la catégorie est proposée partout.</p>
        </div>
      </div>

      <div className="carte">
        <h2>Catégories ({categories?.length ?? 0})</h2>
        {categories === null ? (
          <div className="vide">Chargement…</div>
        ) : categories.length === 0 ? (
          <div className="vide">
            Aucune catégorie. « Installer le jeu de départ » en propose huit — notice, plaque
            signalétique, rapport de contrôle, plan d’accès… — que vous pourrez compléter ou archiver.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Libellé</th>
                <th style={{ width: 240 }}>Code</th>
                <th style={{ width: 200 }}>Proposée pour</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.label}</strong>
                  </td>
                  <td>
                    <code>{c.code}</code>
                  </td>
                  <td>
                    {c.appliesTo.length === 0
                      ? 'Partout'
                      : c.appliesTo
                          .map((a) => TYPES.find((t) => t.code === a)?.libelle ?? a)
                          .join(', ')}
                  </td>
                  <td>
                    <button
                      className="secondaire discret danger-discret"
                      onClick={() => void archiver(c)}
                    >
                      Archiver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
